import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search } from 'lucide-react';
import { useItemCache } from '../context/ItemCacheContext';
import { Item } from '../types/Types';

const PAGE_SIZE = 40;

interface ItemSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: Item) => void;
  currentValue?: string;
  accountingTypeCodePrefix?: string;
  /** Comma-separated accounting-type group keys (e.g. "finished-product,semi-finished"). */
  accountingTypeGroup?: string;
  hasIngredient?: boolean;
  statusMode?: 'notInactive';
}

const ItemSearchModal: React.FC<ItemSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentValue,
  accountingTypeCodePrefix,
  accountingTypeGroup,
  hasIngredient,
  statusMode
}) => {
  const { t } = useTranslation();
  const { searchItems } = useItemCache();
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState<Item[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false); // Prevent duplicate load requests
  const currentSearchRef = useRef(''); // Track current search to prevent stale requests

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setItems([]);
      setSelectedIndex(0);
      setCurrentPage(1);
      setHasMore(false);
      setIsLoading(false);
      setIsLoadingMore(false);
      loadingRef.current = false;
      currentSearchRef.current = '';
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Search with debounce - resets to page 1
  useEffect(() => {
    const searchDebounce = setTimeout(async () => {
      if (isOpen) {
        currentSearchRef.current = searchTerm;
        loadingRef.current = false;
        setIsLoading(true);
        try {
          const result = await searchItems(searchTerm, 1, { accountingTypeCodePrefix, accountingTypeGroup, limit: PAGE_SIZE, hasIngredient, statusMode });
          // Only update if this is still the current search
          if (currentSearchRef.current === searchTerm) {
            setItems(result.items);
            setHasMore(result.hasMore);
            setCurrentPage(1);
            setSelectedIndex(0);
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollTop = 0;
            }
          }
        } finally {
          if (currentSearchRef.current === searchTerm) {
            setIsLoading(false);
          }
        }
      }
    }, 300);

    return () => clearTimeout(searchDebounce);
  }, [searchTerm, isOpen, searchItems, accountingTypeCodePrefix, accountingTypeGroup, hasIngredient, statusMode]);

  // Load more items
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore || isLoading) return;

    loadingRef.current = true;
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    const currentSearch = currentSearchRef.current;

    try {
      const result = await searchItems(currentSearch, nextPage, { accountingTypeCodePrefix, accountingTypeGroup, limit: PAGE_SIZE, hasIngredient, statusMode });
      // Only update if search hasn't changed
      if (currentSearchRef.current === currentSearch) {
        setItems(prev => [...prev, ...result.items]);
        setCurrentPage(nextPage);
        setHasMore(result.hasMore);
      }
    } finally {
      loadingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [currentPage, hasMore, isLoading, searchItems, accountingTypeCodePrefix, accountingTypeGroup, hasIngredient, statusMode]);

  // Safety net: if the loaded rows don't overflow the scroll container, onScroll never
  // fires and pagination would dead-end. Keep loading while there's more and no scrollbar.
  useEffect(() => {
    if (!isOpen || !hasMore || isLoading || isLoadingMore || loadingRef.current) return;

    const el = scrollContainerRef.current;
    if (!el) return;

    if (el.scrollHeight <= el.clientHeight) {
      loadMore();
    }
  }, [items, hasMore, isLoading, isLoadingMore, isOpen, loadMore]);

  // Handle scroll
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 150;

    if (nearBottom && hasMore && !loadingRef.current && !isLoading) {
      loadMore();
    }
  }, [hasMore, isLoading, loadMore]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev < items.length - 1 ? prev + 1 : prev;
          // Scroll to highlighted item with slight delay (like PartnersSearch)
          setTimeout(() => {
            const element = document.querySelector(`[data-row-index="${newIndex}"]`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 50);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : prev;
          // Scroll to highlighted item with slight delay (like PartnersSearch)
          setTimeout(() => {
            const element = document.querySelector(`[data-row-index="${newIndex}"]`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 50);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (items[selectedIndex]) {
          onSelect(items[selectedIndex]);
          onClose();
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  const handleSelect = (item: Item) => {
    onSelect(item);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[70vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex flex-col">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-600" />
              {t('tableHeaders.searchItems') || 'Search Items'}
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              {t('search.keyboardInstructions') || 'Use arrow keys to navigate, Enter to select, Escape to close'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Search Input */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('search.searchItemsPlaceholder') || 'Search by item code, name...'}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-lg"
              autoFocus
            />
          </div>

          {/* Scrollable Results */}
          <div
            ref={scrollContainerRef}
            className="max-h-80 overflow-y-auto border border-gray-200 rounded-xl"
            style={{ minHeight: '250px' }}
            onScroll={handleScroll}
          >
            {isLoading && items.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">{t('search.searching') || 'Searching...'}</span>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left">
                    <th className="py-3 px-4 font-semibold text-gray-700 border-b w-32">
                      {t('tableHeaders.ident') || 'Code'}
                    </th>
                    <th className="py-3 px-4 font-semibold text-gray-700 border-b">
                      {t('tableHeaders.name') || 'Name'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => {
                    const isHighlighted = selectedIndex === index;
                    return (
                      <tr
                        key={item._id}
                        data-row-index={index}
                        onClick={() => handleSelect(item)}
                        className={`border-b border-gray-100 cursor-pointer transition-colors duration-150 ${
                          isHighlighted
                            ? 'bg-blue-50 border-l-4 border-blue-500 shadow-md'
                            : 'hover:bg-blue-50'
                        }`}
                        onMouseEnter={() => {
                          if (selectedIndex !== index) {
                            setSelectedIndex(index);
                          }
                        }}
                      >
                        <td className={`py-3 px-4 font-medium transition-colors duration-150 ${
                          isHighlighted ? 'text-blue-900' : 'text-gray-900'
                        }`}>
                          {item.ident}
                        </td>
                        <td className={`py-3 px-4 transition-colors duration-150 ${
                          isHighlighted ? 'text-blue-800' : 'text-gray-700'
                        }`}>
                          {item.name}
                        </td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && !isLoading && (
                    <tr>
                      <td colSpan={2} className="text-center text-gray-500 py-12">
                        <div className="flex flex-col items-center gap-2">
                          <Search className="w-8 h-8 text-gray-300" />
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Infinite scroll loading indicator */}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-3 border-t border-gray-100">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-xs text-gray-600">{t('search.loadingMore') || 'Loading more...'}</span>
              </div>
            )}
          </div>

          {/* Footer counter */}
          <div className="flex items-center justify-center mt-4 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-600">
              {items.length > 0 && (
                <span>
                  {items.length} {t('common.items') || 'items'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemSearchModal;
